import { TFunction } from '@invision/core';

export interface IdpCallbackQueryParams {
  faultCode?: string;
}

export interface IdpErrorInfo {
  code: string;
  message: string;
}

/**
 * Extract IDP query parameters from URL search string
 * Path parameters should be handled by React Router useParams hook
 */
export function extractIdpParameters(search: string): IdpCallbackQueryParams {
  const queryParams = new URLSearchParams(search);

  return {
    faultCode: queryParams.get('faultCode') || undefined
  };
}

/**
 * Get user-friendly error information for IDP fault codes
 */
export function getIdpErrorInfo(faultCode: string, t: TFunction): IdpErrorInfo {
  const errorCodes = {
    IDP_AUTH_FAILED: 'IDP_AUTH_FAILED',
    IDP_CONFIG_ERROR: 'IDP_CONFIG_ERROR',
    USER_CANCELLED: 'USER_CANCELLED',
    SESSION_EXPIRED: 'SESSION_EXPIRED',
    INVALID_REQUEST: 'INVALID_REQUEST',
    ACCESS_DENIED: '133',
    INVALID_RESPONSE: 'INVALID_RESPONSE',
    UNKNOWN_ERROR: 'UNKNOWN_ERROR'
  } as const;

  const code = Object.values(errorCodes).includes(faultCode as any)
    ? faultCode
    : errorCodes.UNKNOWN_ERROR;

  const messages: Record<string, string> = {
    IDP_AUTH_FAILED: t('Identity provider authentication failed'),
    IDP_CONFIG_ERROR: t('Identity provider configuration error'),
    USER_CANCELLED: t('Authentication was cancelled'),
    SESSION_EXPIRED: t('Authentication session expired'),
    INVALID_REQUEST: t('Invalid authentication request'),
    [errorCodes.ACCESS_DENIED]: t('Access denied, insufficient access'),
    INVALID_RESPONSE: t('Invalid response from identity provider'),
    UNKNOWN_ERROR: t('Authentication failed with error: {{code}}', {
      code: faultCode
    })
  };

  return {
    code,
    message: messages[code] || messages[errorCodes.UNKNOWN_ERROR]
  };
}

/**
 * Decode Base64 nonce token safely
 */
export function decodeNonceToken(nonceToken: string): string {
  try {
    return window.atob(nonceToken);
  } catch (_error) {
    throw new Error('Invalid nonce token format');
  }
}
